import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, TextField, InputAdornment } from '@mui/material';

export default function ValidatedTextInput({
    value,
    onChangeText,
    checkUrl,
    checkFunction,
    requestBodyBuilder,
    minLength = 1,
    debounceMs = 500,
    disabled = false,
    onFocus,
    successStatus = 200,
    successField,
    successValue = true,
    placeholder = '',
    maxLength,
    style,
    textFieldProps = {},
}) {
    const [loading, setLoading] = useState(false);
    const [valid, setValid] = useState(null); // null | true | false
    const timerRef = useRef(null);
    const inputRef = useRef(null);

    const resetState = () => {
        clearTimeout(timerRef.current);
        setLoading(false);
        setValid(null);
    };

    const runCheck = async (text) => {
        const trimmed = String(text || '').trim();

        if (!trimmed) {
            resetState();
            return;
        }

        if (trimmed.length < minLength) {
            clearTimeout(timerRef.current);
            setLoading(false);
            setValid(null);
            return;
        }

        setLoading(true);
        setValid(null);

        try {
            let ok;

            if (typeof checkFunction === 'function') {
                ok = await checkFunction(trimmed);
            } else {
                const res = await fetch(checkUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBodyBuilder(trimmed)),
                });

                ok = res.status === successStatus;

                if (ok && successField) {
                    const json = await res.json();
                    ok = json?.[successField] === successValue;
                }
            }

            setValid(Boolean(ok));
        } catch (error) {
            setValid(false);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        const text = event.target.value;

        if (maxLength != null && text.length > maxLength) {
            return;
        }

        onChangeText?.(text.toUpperCase());

        const trimmed = String(text || '').trim();

        clearTimeout(timerRef.current);

        if (!trimmed) {
            resetState();
            return;
        }

        if (trimmed.length < minLength) {
            setLoading(false);
            setValid(null);
            return;
        }

        timerRef.current = setTimeout(() => {
            runCheck(text);
        }, debounceMs);
    };

    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
        };
    }, []);

    return (
        <Box style={style}>
            <TextField
                fullWidth
                value={value ?? ''}
                onChange={handleChange}
                placeholder={placeholder}
                // disabled={disabled}
                inputRef={inputRef}
                onFocus={(e) => {
                    onFocus?.(e);
                    console.log("Input focused, current value: ", e.target.value, " INPUTREF CURRENT: ", inputRef.current, " DISABLED: ", disabled);
                    if (disabled && inputRef.current) {
                        console.log("Blurring input due to disabled state.");
                        inputRef.current.blur();
                    }
                }}
                inputProps={{
                    maxLength: maxLength ?? undefined,
                    style: {
                        textTransform: 'uppercase',
                    },
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {loading && <CircularProgress size={18} />}
                            {!loading && valid === true && <span style={{ fontSize: 18 }}>✅</span>}
                            {!loading && valid === false && <span style={{ fontSize: 18 }}>❌</span>}
                        </InputAdornment>
                    ),
                }}
                {...textFieldProps}
            />
        </Box>
    );
}